type InquiryPayload = {
  name: string;
  email?: string;
  phone: string;
  course?: string;
  message?: string;
  website?: string;
  formStartedAt?: number;
  turnstileToken?: string;
};

type ApplicationPayload = {
  name: string;
  email: string;
  phone: string;
  course: string;
  qualification?: string;
  experience?: string;
  preferredMode?: string;
  callbackTime?: string;
  message?: string;
  website?: string;
  formStartedAt?: number;
  turnstileToken?: string;
};

type NewsletterPayload = {
  email: string;
  website?: string;
  formStartedAt?: number;
  turnstileToken?: string;
};

export type AdminSubmission = {
  id: string;
  type: 'inquiry' | 'application' | 'newsletter';
  createdAt: string;
  updatedAt?: string;
  status?: string;
  note?: string;
  name?: string;
  email?: string;
  phone?: string;
  course?: string;
  qualification?: string;
  experience?: string;
  preferredMode?: string;
  callbackTime?: string;
  message?: string;
  ip?: string;
  userAgent?: string;
  verification?: 'screened' | 'turnstile' | 'unverified';
};

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  readTime: string;
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string;
  imageUrl?: string;
  imageAlt?: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  published: boolean;
};

type BlogPayload = {
  title: string;
  slug?: string;
  excerpt: string;
  category: string;
  author: string;
  readTime: string;
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string;
  imageData?: string;
  imageAlt?: string;
  content: string;
  published?: boolean;
  removeImage?: boolean;
};

/** The cookie carries the session; a literal token is only for non-browser callers. */
const authHeader = (token?: string): Record<string, string> =>
  token && token !== 'cookie-session' ? { Authorization: `Bearer ${token}` } : {};

/**
 * All admin auth rides on an HttpOnly session cookie, so every request must
 * send credentials.
 */
const submitJson = async (path: string, payload: unknown) => {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Submission failed. Please try again.');
  }

  return data;
};

type InboxNotification = {
  endpoint: string;
  payload: Record<string, unknown>;
};

const submitPublicForm = async (path: string, payload: unknown) => {
  const data = await submitJson(path, payload) as { notification?: InboxNotification };
  if (data.notification) {
    const response = await fetch(data.notification.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data.notification.payload),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.success === false) {
      throw new Error(result.message || 'Your form was saved, but the email notification could not be sent.');
    }
  }
  const values = payload as { email?: string };
  window.dispatchEvent(new CustomEvent('asb:form-submit', { detail: { formType: path.split('/').pop(), email: values.email || '' } }));
  return data;
};

/** Total clicks plus distinct visitors, so repeat clicks by one person are visible. */
export type ClickTally = { total: number; unique: number; repeats: number; last: string };
export type AnalyticsClick = {
  id: string; createdAt: string; path: string; title?: string; label: string; target: string;
  channel: string; placement?: string; location: string; device?: string; os?: string; browser?: string;
  visitor: string; repeat: number; ip?: string;
};
export type AnalyticsReport = {
  range: { from: string; to: string };
  summary: {
    pageViews: number; sessions: number; visitors: number; formSubmissions: number;
    clicks: number; uniqueClickers: number; contactClicks: number; contactVisitors: number;
    directContacts: number; averageScrollDepth: number; averageDurationSeconds: number;
  };
  channels: Array<ClickTally & { channel: string }>;
  topClicks: Array<ClickTally & { channel: string; label: string; target: string }>;
  locations: Array<{ location: string; visitors: number; pageViews: number; contactClicks: number; formSubmissions: number }>;
  clickLog: AnalyticsClick[];
  devices: [string, number][];
  topPages: [string, number][];
  keywords: [string, number][];
  sources: [string, number][];
  submissions: Array<Record<string, unknown>>;
  recentEvents: Array<Record<string, unknown>>;
};
export const fetchAdminAnalytics = async (token:string,from?:string,to?:string):Promise<AnalyticsReport> => { const query=new URLSearchParams();if(from)query.set('from',from);if(to)query.set('to',to);const response=await fetch(`/api/admin/analytics?${query}`,{headers:authHeader(token),credentials:'same-origin'}); if(!response.ok) throw new Error('Unable to load analytics.'); return response.json(); };

export const submitInquiry = (payload: InquiryPayload) => submitPublicForm('/api/inquiries', payload);

export const submitApplication = (payload: ApplicationPayload) =>
  submitPublicForm('/api/applications', payload);

export const submitNewsletter = (payload: NewsletterPayload) =>
  submitPublicForm('/api/newsletters', payload);

/** The API answered 404: the record does not exist, so the page may be noindexed. */
export class NotFoundError extends Error {}

export const isNotFoundError = (error: unknown): error is NotFoundError => error instanceof NotFoundError;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * GET a single public record. Rate limits (429), server errors and network
 * failures are retried, because crawlers render many pages from shared IPs and
 * a transient failure must never be reported as a missing page.
 */
const fetchPublicRecord = async <T>(url: string, notFoundMessage: string): Promise<T> => {
  const delays = [600, 1800, 4000];
  for (let attempt = 0; ; attempt += 1) {
    let response: Response | undefined;
    try {
      response = await fetch(url);
    } catch (error) {
      if (attempt >= delays.length) throw error;
    }
    if (response?.ok) return response.json();
    if (response?.status === 404) throw new NotFoundError(notFoundMessage);
    if (response && response.status !== 429 && response.status < 500) throw new Error(notFoundMessage);
    if (attempt >= delays.length) throw new Error('Temporarily unable to load this page. Please try again.');
    const retryAfter = Number(response?.headers.get('Retry-After'));
    await wait(Number.isFinite(retryAfter) && retryAfter > 0 ? Math.min(retryAfter * 1000, 5000) : delays[attempt]);
  }
};

export const fetchBlogs = async (): Promise<BlogPost[]> => {
  const response = await fetch('/api/blogs');
  if (!response.ok) throw new Error('Unable to load blogs.');
  return response.json();
};

export const fetchBlog = (slug: string): Promise<BlogPost> =>
  fetchPublicRecord(`/api/blogs/${encodeURIComponent(slug)}`, 'Blog not found.');

export const adminLogin = async (username: string, password: string): Promise<string> => {
  const data = await submitJson('/api/admin/login', { username, password });
  return data.token;
};

/** Ends the server-side session and clears the auth cookie. */
export const logoutRequest = async () => {
  await fetch('/api/admin/logout', { method: 'POST', credentials: 'same-origin' }).catch(
    () => undefined,
  );
};

/** Returns true when the session cookie is still valid. */
export const checkSession = async (): Promise<boolean> => {
  const response = await fetch('/api/admin/session', { credentials: 'same-origin' }).catch(
    () => null,
  );
  return Boolean(response?.ok);
};

export const createBlog = async (payload: BlogPayload, token: string): Promise<BlogPost> => {
  const response = await fetch('/api/admin/blogs', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(token),
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Unable to save blog.');
  }

  return data.blog;
};

export const fetchAdminBlogs = async (token: string): Promise<BlogPost[]> => {
  const response = await fetch('/api/admin/blogs', {
    headers: authHeader(token),
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error('Unable to load admin blogs.');
  return response.json();
};

export const updateBlog = async (slug: string, payload: BlogPayload, token: string): Promise<BlogPost> => {
  const response = await fetch(`/api/admin/blogs/${slug}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(token),
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Unable to update blog.');
  }

  return data.blog;
};

export const deleteBlog = async (slug: string, token: string) => {
  const response = await fetch(`/api/admin/blogs/${slug}`, {
    method: 'DELETE',
    headers: authHeader(token),
    credentials: 'same-origin',
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Unable to delete blog.');
  }

  return data;
};

export const fetchAdminSubmissions = async (token: string): Promise<AdminSubmission[]> => {
  const response = await fetch('/api/admin/submissions', {
    headers: authHeader(token),
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error('Unable to load submissions.');
  return response.json();
};

export const updateAdminSubmission = async (
  submission: Pick<AdminSubmission, 'id' | 'type'> & { status?: string; note?: string },
  token: string,
): Promise<AdminSubmission> => {
  const response = await fetch(`/api/admin/submissions/${submission.type}/${submission.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(token),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ status: submission.status, note: submission.note }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Unable to update submission.');
  }

  return data.submission;
};

/* ------------------------------------------------------------------ *
 * Courses
 * ------------------------------------------------------------------ */

export type CourseFaq = { q: string; a: string };

/** Full course record, as stored and edited in the admin. */
export type CatalogueEntry = {
  id: string;
  slug: string;
  title: string;
  category: string;
  categoryLabel: string;
  icon: string;
  description: string;
  overview: string;
  duration: string;
  mode: string;
  internship: boolean;
  syllabus: string[];
  tools: string[];
  careers: string[];
  whoShouldJoin: string[];
  learningOutcomes: string[];
  prerequisites: string[];
  projects: string[];
  certificate: string;
  faqs: CourseFaq[];
  content: string;
  imageUrl: string;
  imageAlt: string;
  secondaryImageUrl: string;
  secondaryImageAlt: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
};

/** The trimmed shape the listing pages fetch (`?summary=1`). */
export type CatalogueSummary = Pick<
  CatalogueEntry,
  | 'id' | 'slug' | 'title' | 'category' | 'categoryLabel'
  | 'description' | 'duration' | 'mode' | 'internship' | 'icon'
  | 'imageUrl' | 'imageAlt'
>;

export type CourseCategoryInfo = { id: string; label: string; count: number };

/** What the admin form sends. Images travel as base64 data URIs. */
export type CataloguePayload = Partial<Omit<CatalogueEntry, 'createdAt' | 'updatedAt'>> & {
  title: string;
  description: string;
  imageData?: string;
  secondaryImageData?: string;
  removeImage?: boolean;
  removeSecondaryImage?: boolean;
};

export type Paginated<T> = {
  items: T[];
  page: number;
  perPage: number;
  total: number;
  pages: number;
};

export const fetchCourseSummaries = async (category?: string): Promise<CatalogueSummary[]> => {
  const query = new URLSearchParams({ summary: '1' });
  if (category) query.set('category', category);
  const response = await fetch(`/api/courses?${query}`);
  if (!response.ok) throw new Error('Unable to load courses.');
  return response.json();
};

export const fetchCourse = (slug: string): Promise<CatalogueEntry> =>
  fetchPublicRecord(`/api/courses/${encodeURIComponent(slug)}`, 'Course not found.');

/* --- generic catalogue admin (serves both courses and training) --- */

export const fetchAdminEntries = async (
  apiPath: string,
  token: string,
  { page = 1, perPage = 20, search = '', category = '' } = {},
): Promise<Paginated<CatalogueEntry>> => {
  const query = new URLSearchParams({ page: String(page), perPage: String(perPage) });
  if (search) query.set('search', search);
  if (category) query.set('category', category);

  const response = await fetch(`/api/admin/${apiPath}?${query}`, {
    headers: authHeader(token),
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('Unable to load entries.');
  return response.json();
};

export const createEntry = async (
  apiPath: string,
  payload: CataloguePayload,
  token: string,
): Promise<CatalogueEntry> => {
  const response = await fetch(`/api/admin/${apiPath}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Unable to save.');
  return data.item;
};

export const updateEntry = async (
  apiPath: string,
  slug: string,
  payload: CataloguePayload,
  token: string,
): Promise<CatalogueEntry> => {
  const response = await fetch(`/api/admin/${apiPath}/${slug}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader(token) },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Unable to update.');
  return data.item;
};

export const deleteEntry = async (apiPath: string, slug: string, token: string) => {
  const response = await fetch(`/api/admin/${apiPath}/${slug}`, {
    method: 'DELETE',
    headers: authHeader(token),
    credentials: 'same-origin',
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Unable to delete.');
  return data;
};

/* --- training: public reads --------------------------------------- */

export const fetchTrainingSummaries = async (category?: string): Promise<CatalogueSummary[]> => {
  const query = new URLSearchParams({ summary: '1' });
  if (category) query.set('category', category);
  const response = await fetch(`/api/training?${query}`);
  if (!response.ok) throw new Error('Unable to load training programmes.');
  return response.json();
};

export const fetchTrainingProgramme = (slug: string): Promise<CatalogueEntry> =>
  fetchPublicRecord(`/api/training/${encodeURIComponent(slug)}`, 'Training programme not found.');

/* --- names kept for existing callers ------------------------------ */

export type Course = CatalogueEntry;
export type CourseSummary = CatalogueSummary;
export type CoursePayload = CataloguePayload;
